/**
 * (Spostato il 29/10/2026 da `console-app/src/components/mappa/` in `shared/mappa/`, identico: lo usano la mappa delle
 * aziende della console e la cartina delle aree di interesse del gestionale. Puro, niente oltre ES2017.)
 *
 * La geometria della mappa delle aziende: proiezione, confini delle regioni, reticolo. Pura, senza React.
 *
 * ## Perché una mappa nostra e non Google Maps
 * La mappa deve aprirsi anche dal link condiviso, per chi non ha un accesso: con Google la chiave del browser
 * finirebbe in una pagina pubblica, ogni apertura costerebbe e lo stile sarebbe quello di tutte le mappe. A scala
 * nazionale servono solo i confini, che il gestionale ha già (`public/confini/regioni.json`, ISTAT semplificati a circa
 * 400 m da `scripts/installazioni/confini-italia.ts`): li disegniamo noi in SVG.
 *
 * ## La proiezione
 * Mercatore, come le mappe che tutti hanno in mente: a queste latitudini l'Italia ha la forma che ci si aspetta. Le
 * coordinate «del mondo» sono in millesimi di radiante (x verso est, y verso sud); la camera (`camera.ts`) le porta in
 * pixel.
 */

export interface Punto {
  x: number;
  y: number;
}

export interface Riquadro {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

const SCALA = 1000;
const GRADI = Math.PI / 180;

export function proietta(lng: number, lat: number): Punto {
  return {
    x: lng * GRADI * SCALA,
    y: -Math.log(Math.tan(Math.PI / 4 + (lat * GRADI) / 2)) * SCALA,
  };
}

export function riquadroGeografico(lngMin: number, latMin: number, lngMax: number, latMax: number): Riquadro {
  const a = proietta(lngMin, latMax);
  const b = proietta(lngMax, latMin);
  return { minX: a.x, minY: a.y, maxX: b.x, maxY: b.y };
}

/** L'Italia intera, dalle Alpi a Lampedusa. */
export const RIQUADRO_ITALIA = riquadroGeografico(6.6, 35.45, 18.55, 47.1);

// ───────────────────────────────────────────────────────────── confini

type Anello = Array<[number, number]>;
/** Un poligono: il primo anello è il contorno, gli altri sono buchi. */
type Poligono = Anello[];

export interface Regione {
  chiave: string;
  poligoni: Poligono[];
  /** Il percorso SVG nelle coordinate del mondo. */
  percorso: string;
}

interface ElementoGeoJson {
  properties?: { chiave?: unknown };
  geometry?: { type?: unknown; coordinates?: unknown };
}

const arrotonda = (v: number, f = 10) => Math.round(v * f) / f;

function percorsoDiPoligoni(poligoni: Poligono[], f = 10): string {
  const pezzi: string[] = [];
  for (const poligono of poligoni) {
    for (const anello of poligono) {
      if (anello.length < 3) continue;
      const punti = anello.map(([lng, lat]) => {
        const p = proietta(lng, lat);
        return `${arrotonda(p.x, f)} ${arrotonda(p.y, f)}`;
      });
      pezzi.push(`M${punti.join('L')}Z`);
    }
  }
  return pezzi.join('');
}

/**
 * Le regioni dal GeoJSON dei confini; un elemento di forma inattesa si salta. `decimali` = la precisione del percorso
 * nelle unità del mondo: 1 (circa 600 m) basta per le regioni viste da lontano; per i comuni, che si guardano da vicino,
 * ne servono 2 (circa 60 m, come la semplificazione dei loro confini).
 */
export function regioniDaGeoJson(raccolta: { features?: unknown }, decimali = 1): Regione[] {
  const f = Math.pow(10, decimali);
  const elementi = Array.isArray(raccolta.features) ? (raccolta.features as ElementoGeoJson[]) : [];
  const regioni: Regione[] = [];
  for (const e of elementi) {
    const chiave = typeof e.properties?.chiave === 'string' ? e.properties.chiave : null;
    const tipo = e.geometry?.type;
    const coordinate = e.geometry?.coordinates;
    if (!chiave || !Array.isArray(coordinate)) continue;
    const poligoni = (tipo === 'Polygon' ? [coordinate] : tipo === 'MultiPolygon' ? coordinate : []) as Poligono[];
    if (poligoni.length === 0) continue;
    regioni.push({ chiave, poligoni, percorso: percorsoDiPoligoni(poligoni, f) });
  }
  return regioni;
}

/** Il riquadro, nelle coordinate del mondo, che contiene tutti i confini dati (null se non ce n'è nessuno). */
export function riquadroDi(regioni: readonly Regione[]): Riquadro | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const r of regioni) {
    for (const poligono of r.poligoni) {
      for (const [lng, lat] of poligono[0] || []) {
        const p = proietta(lng, lat);
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
      }
    }
  }
  return minX === Infinity ? null : { minX, minY, maxX, maxY };
}

function dentroLAnello(lng: number, lat: number, anello: Anello): boolean {
  let dentro = false;
  for (let i = 0, j = anello.length - 1; i < anello.length; j = i++) {
    const [xi, yi] = anello[i];
    const [xj, yj] = anello[j];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) dentro = !dentro;
  }
  return dentro;
}

/** La regione che contiene il punto, o null (in mare, fuori dall'Italia). */
export function regioneDelPunto(regioni: readonly Regione[], lng: number, lat: number): string | null {
  for (const r of regioni) {
    for (const [contorno, ...buchi] of r.poligoni) {
      if (contorno && dentroLAnello(lng, lat, contorno) && !buchi.some((b) => dentroLAnello(lng, lat, b))) return r.chiave;
    }
  }
  return null;
}

/** Il reticolo di meridiani e paralleli, un grado alla volta, attorno all'Italia. */
export function reticolo(): string {
  const lngMin = 2;
  const lngMax = 23;
  const latMin = 33;
  const latMax = 50;
  const pezzi: string[] = [];
  for (let lng = lngMin; lng <= lngMax; lng += 1) {
    const a = proietta(lng, latMax);
    const b = proietta(lng, latMin);
    pezzi.push(`M${arrotonda(a.x)} ${arrotonda(a.y)}V${arrotonda(b.y)}`);
  }
  for (let lat = latMin; lat <= latMax; lat += 1) {
    const a = proietta(lngMin, lat);
    const b = proietta(lngMax, lat);
    pezzi.push(`M${arrotonda(a.x)} ${arrotonda(a.y)}H${arrotonda(b.x)}`);
  }
  return pezzi.join('');
}
